import "controls/FormDialog";

import { gql } from "@apollo/client";
import Field from "controls/Field";
import Message from "controls/Message";
import { formatInTimeZone } from "date-fns-tz";
import getUiString from "helpers/getUiString";
import useData from "hooks/useData";
import useTimeZone from "hooks/useTimeZone";
import DateTimeInput from "inputs/DateTimeInput";
import TimeInput from "inputs/TimeInput";
import React, { useLayoutEffect, useState } from "react";
import ReadminObjectInput from "readmin_pages/shared/ReadminObjectInput";
import ReadminObjectsTable, { ReadminObjectsTableFragment } from "readmin_pages/shared/ReadminObjectsTable";

export const GameCutFormFragment = gql`
  fragment GameCutFormFragment on Game {
    id
    court {
      id
    }
    playedAt
    completedAt
  }
`;

const ISO_DATE_FORMAT = "yyyy-MM-dd";

export default function GameCutForm({ game, onRecordingsAvailableChange }) {
  const [court, courtSet] = useState(null);
  const [surface, surfaceSet] = useState(null);
  const [playedAt, playedAtSet] = useState(null);
  const [completedAt, completedAtSet] = useState(null);
  const timezone = useTimeZone();

  const date = playedAt && formatInTimeZone(playedAt, timezone, ISO_DATE_FORMAT);
  const skipDataRecordings = !court || !date || !completedAt || !playedAt;
  const [dataRecordings] = useData(
    gql`
      query GameCutForm_recordings(
        $courtId: ID!
        $dateRange: DateRange!
        $playedAt: ISO8601DateTime!
        $completedAt: ISO8601DateTime!
      ) {
        court(id: $courtId) {
          id
          surface {
            id
          }
          sameDayRecordings: recordings(range: $dateRange) {
            id
            ...ReadminObjectsTableFragment
          }
          recordingsAny(playedAt: $playedAt, completedAt: $completedAt, deleted: false)
        }
      }
      ${ReadminObjectsTableFragment}
    `,
    {
      courtId: court?.id,
      dateRange: date && {
        from: date,
        to: date,
      },
      playedAt,
      completedAt,
    },
    {
      skip: skipDataRecordings,
    },
  );
  const courtSurfaceId = dataRecordings?.court?.surface?.id;

  useLayoutEffect(() => {
    courtSet(game?.court ? { id: game.court.id, __typename: "Court" } : null);
    playedAtSet(game?.playedAt);
    completedAtSet(game?.completedAt);
  }, [game?.id]);

  useLayoutEffect(() => {
    surfaceSet(courtSurfaceId ? { id: courtSurfaceId, __typename: "Surface" } : null);
  }, [courtSurfaceId]);

  const recordingsAvailable = dataRecordings?.court?.recordingsAny;
  useLayoutEffect(() => {
    if (![null, undefined].includes(recordingsAvailable)) {
      onRecordingsAvailableChange?.(recordingsAvailable);
    }
  }, [recordingsAvailable]);

  return (
    <>
      <Field
        name="court"
        label="Court"
        value={court}
        onChange={courtSet}
        required
        input={<ReadminObjectInput optionsTypename="Court" />}
      />
      <Field
        name="playedAt"
        value={playedAt}
        onChange={playedAtSet}
        required
        label="Start time"
        input={<DateTimeInput />}
      />
      <Field
        name="completedAt"
        value={completedAt}
        onChange={completedAtSet}
        required
        label="End time"
        input={<TimeInput date={date} />}
      />
      {dataRecordings?.court?.recordingsAny && (
        <Message title="Recordings available" content={getUiString("readmin.game.cut_recordings_available")} />
      )}
      {dataRecordings?.court?.recordingsAny === false && (
        <Message
          type="error"
          title="Recordings not available"
          content={getUiString("readmin.game.cut_no_recordings_error")}
        />
      )}
      {!skipDataRecordings && (
        <ReadminObjectsTable
          ignoreAssociationObjects={[court]}
          header="Recordings made on the same day"
          rows={dataRecordings?.court?.sameDayRecordings}
        />
      )}
      <Field
        name="surface"
        label="Surface"
        value={surface}
        onChange={surfaceSet}
        required
        disabled={!courtSurfaceId}
        input={<ReadminObjectInput optionsTypename="Surface" />}
      />
    </>
  );
}
